import { useState, useRef, useCallback, useEffect } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { useGoogleOneTap } from '../../hooks/useGoogleOneTap';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { cpInitiateLineLogin, storeLineState } from '@/lib/api/controlPlaneClient';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';
import posthog from 'posthog-js';

interface StepAuthMethodProps {
  onGoogleCredential: (credential: string) => void;
  onEmailFallback: () => void;
}

export function StepAuthMethod({ onGoogleCredential, onEmailFallback }: StepAuthMethodProps) {
  const { intent } = useRegistrationStore();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  const handleGoogleSuccess = (credential: string, selectBy: string) => {
    setGoogleLoading(true);
    setError(null);

    if (posthog.__loaded) {
      posthog.capture('registration_method_selected', { method: 'google', select_by: selectBy });
    }

    onGoogleCredential(credential);
    // Loading state cleared by parent after store update
  };

  const { isReady, renderButton } = useGoogleOneTap({
    onSuccess: handleGoogleSuccess,
    onError: (msg) => setError(msg),
    autoPrompt: true,
    disabled: false,
  });

  // Render the real GIS button as an invisible overlay on top of our styled button.
  // The user's real click lands directly on the GIS button (preserving user gesture),
  // which avoids popup blocker issues that occur with programmatic .click() proxying.
  const googleBtnCallback = useCallback(
    (node: HTMLDivElement | null) => {
      googleBtnRef.current = node;
      if (node && isReady) {
        renderButton(node);
      }
    },
    [isReady, renderButton],
  );

  // Re-render the GIS button when isReady changes (e.g. after back-navigation)
  useEffect(() => {
    if (isReady && googleBtnRef.current) {
      renderButton(googleBtnRef.current);
    }
  }, [isReady, renderButton]);

  const handleLineClick = async () => {
    setLineLoading(true);
    setError(null);

    if (posthog.__loaded) {
      posthog.capture('registration_method_selected', { method: 'line' });
    }

    try {
      const { auth_url, state } = await cpInitiateLineLogin('/register', intent || undefined);
      storeLineState(state, intent || undefined);
      window.location.href = auth_url;
    } catch (err: any) {
      setError(err.message || t.lineLoginFailed);
      setLineLoading(false);
    }
  };

  const handleEmailClick = () => {
    if (posthog.__loaded) {
      posthog.capture('registration_method_selected', { method: 'email' });
    }
    onEmailFallback();
  };

  const anyLoading = googleLoading || lineLoading;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground md:text-center">
          {t.createAccount}
        </h2>
      </div>

      <div className="space-y-3">
        {/* Google — styled button with invisible GIS button overlaid on top.
            The real GIS button captures the user's direct click (preserving user gesture
            for popup OAuth). Our styled button is visible underneath for consistent styling. */}
        <div className="relative">
          <button
            type="button"
            disabled={anyLoading}
            className="w-full flex items-center justify-center gap-3 rounded-md border border-border bg-background px-4 py-3.5 text-foreground font-medium text-base transition-colors hover:bg-accent active:bg-accent/80 disabled:opacity-50"
          >
            {googleLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <GoogleIcon className="h-5 w-5" />
            )}
            {t.continueWithGoogle}
          </button>
          {/* GIS rendered button overlay — invisible, captures real user click.
              Must have opacity:0 (not display:none) so GIS can measure offsetWidth. */}
          <div
            ref={googleBtnCallback}
            className="absolute inset-0 rounded-md [&>div]:!h-full [&_iframe]:!h-full"
            style={{ opacity: 0 }}
            aria-hidden="true"
          />
        </div>

        {/* LINE */}
        <button
          type="button"
          onClick={handleLineClick}
          disabled={anyLoading}
          className="w-full flex items-center justify-center gap-3 rounded-md bg-[#06C755] px-4 py-3.5 text-white font-medium text-base transition-colors hover:bg-[#05b34d] active:bg-[#04a044] disabled:opacity-50"
        >
          {lineLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <LineIcon className="h-5 w-5" />
          )}
          {t.continueWithLine}
        </button>

        {/* Email fallback */}
        <button
          type="button"
          onClick={handleEmailClick}
          disabled={anyLoading}
          className="w-full flex items-center justify-center gap-3 rounded-md border border-border bg-background px-4 py-3.5 text-foreground font-medium text-base transition-colors hover:bg-accent active:bg-accent/80 disabled:opacity-50"
        >
          <Mail className="h-5 w-5" />
          {t.continueWithEmail}
        </button>
      </div>

      {error && <p className="text-sm text-destructive text-center">{error}</p>}
    </div>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function LineIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  );
}
